import React, { useContext, useEffect } from 'react';
import classes from './OnboardindContent.module.scss';
import { OnboardingContext, OnboardingVersionType } from '../states/OnboardingProvider';
import {
  getCongratsKey,
  InitialOnboardingSteps,
  OnboardingStaticContent,
  SharedContentData,
  StepDataType
} from '../constants/content';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { Button } from '../../../components/actions/Button';
import { IconsEnum } from '../../../constants/icons';
import {
  PostSource,
  useCompleteOnboardingMutation,
  useGetSocialProfilesQuery,
  UserOrganizationRole,
  useSeeOnboardingStepMutation
} from '../../../models/graphql';
import { AppContext, changeSelectedWorkspaceId } from '../../../providers/AppProvider';
import { useOpenPostEditor } from '../../../hooks/useOpenPostEditor';
import { isPostEditorActive } from '../helpers/isPostEditorActive';
import { TypewritterAnimation } from '../TypewritterAnimation';
import { useClosePostEditor } from '../../../hooks/useClosePostEditor';
import { PostEditorContextEnum } from '../../../models/post-editor';
import { useUserData } from '../../../hooks/useUserData';
import { completeOnboarding } from '../helpers/completeOnboarding';
import { gqlCompleteStep } from '../states/reducers/gotoStep.reducer';

const getContentForStep = (version: OnboardingVersionType, stepIndex: number): StepDataType => {
  return InitialOnboardingSteps[`v${version}`][stepIndex].content;
};

export type OnboardindContentViewProps = {
  //
};

export const OnboardindContentView = (_props: OnboardindContentViewProps): JSX.Element => {
  const { userData } = useUserData();
  const { pathname: currentRoute } = useLocation();
  const navigate = useNavigate();
  const { postEditorContext, selectedWorkspaceId } = useContext(AppContext);
  const [completeMutation] = useCompleteOnboardingMutation();
  const [completeStepMutation] = useSeeOnboardingStepMutation();
  const { openNewPost } = useOpenPostEditor();
  const { closePostEditor } = useClosePostEditor();
  const { state, dispatch } = useContext(OnboardingContext);
  const { version, progress, activeStepIndex, isCongrats, stepsCompleted } = state;
  const isPaused = activeStepIndex < 0;
  const { paused, congrats } = OnboardingStaticContent[`v${version}`];

  const organizations = userData?.organizations;
  const switchOrganizationWsId =
    organizations && organizations.length > 1
      ? organizations.find(org => org.myUserAssignment && org.myUserAssignment.role !== UserOrganizationRole.OWNER)
          ?.workspaces[0].id || ''
      : '';

  const getSocialProfilesData = useGetSocialProfilesQuery({ variables: { workspaceId: selectedWorkspaceId } });
  const isOnProfilesPage =
    currentRoute === '/settings/profiles' && postEditorContext.type === PostEditorContextEnum.undefined;
  const { data: socialProfilesData, loading: loadingProfiles } = getSocialProfilesData || {};
  const { socialProfiles } = socialProfilesData?.workspace || {};
  const addedProfile = socialProfiles && socialProfiles.some(profile => !profile.onboardingContent);
  const congratsKey = getCongratsKey(version);

  useEffect(() => {
    if (isCongrats && isOnProfilesPage && addedProfile && userData) {
      completeOnboarding({ completeMutation, user: userData });
    }
  }, [isCongrats, isOnProfilesPage, addedProfile, userData, completeMutation]);

  let data: SharedContentData = isCongrats ? congrats : isPaused ? paused : getContentForStep(version, activeStepIndex);
  const { content, learnMoreArticle, learnMoreText } = data;
  const uncheckedIndex = progress.findIndex(step => !step.completed);
  const isLastStep =
    uncheckedIndex > -1 && uncheckedIndex + 1 === progress.length && activeStepIndex === uncheckedIndex;

  const gotoNextStepOrComplete = () => {
    if (!userData) return;
    const shouldRun = uncheckedIndex > -1 && activeStepIndex !== uncheckedIndex && !isLastStep;
    const targetRoute = uncheckedIndex > -1 ? progress[uncheckedIndex].route : null;
    if (shouldRun) {
      if (targetRoute && targetRoute !== currentRoute) {
        navigate(targetRoute);
      } else if (!targetRoute && !isPostEditorActive(postEditorContext)) {
        // it is post editor so open it
        openNewPost({ source: PostSource.POST_EDITOR });
        dispatch({ type: 'setActiveStepIndex', payload: uncheckedIndex });
      }
    } else if (isLastStep) {
      closePostEditor();
      gqlCompleteStep(completeStepMutation, congratsKey, userData.id);
    } else if (stepsCompleted) {
      gqlCompleteStep(completeStepMutation, congratsKey, userData.id);
    }
  };
  return (
    <div className={classes.OnboardindContent}>
      <div className={'grid-x align-middle align-justify'}>
        <div className="cell small-12 tablet-portrait-auto">
          <TypewritterAnimation content={content} learnMoreArticle={learnMoreArticle} learnMoreText={learnMoreText} />
        </div>
        {isCongrats ? (
          isOnProfilesPage ? (
            <></>
          ) : (
            <div
              className={
                'cell small-12 tablet-portrait-shrink text-right margin-top-small-16 margin-top-tablet-portrait-0'
              }>
              {switchOrganizationWsId ? (
                <Button
                  loading={loadingProfiles}
                  action={() => {
                    if (userData) {
                      completeOnboarding({ completeMutation, user: userData }).then(() =>
                        changeSelectedWorkspaceId(switchOrganizationWsId)
                      );
                    }
                  }}
                  icon={{ side: 'right', value: IconsEnum.arrowRight }}
                  type={'button'}
                  variant={'solid'}
                  size={'medium'}
                  className={classes.NextButton}
                  color={ButtonColorsEnum.white}>
                  {'Switch Organization'}
                </Button>
              ) : (
                <Button
                  loading={loadingProfiles}
                  action={() => {
                    if (addedProfile && userData) {
                      completeOnboarding({ completeMutation, user: userData });
                    } else {
                      navigate('/settings/profiles');
                    }
                  }}
                  icon={{ side: 'right', value: IconsEnum.arrowRight }}
                  type={'button'}
                  variant={'solid'}
                  size={'medium'}
                  className={classes.NextButton}
                  color={ButtonColorsEnum.white}>
                  {addedProfile ? 'Got It' : 'Connect Profiles'}
                </Button>
              )}
            </div>
          )
        ) : (
          <>
            <div className={'cell small-4 tablet-portrait-shrink margin-top-small-16 margin-top-tablet-portrait-0'}>
              {isPaused ? (
                <span className={'show-for-sr'}></span>
              ) : (
                <div className={classes.StepProgressSmall}>
                  {activeStepIndex + 1} / {progress.length}
                </div>
              )}
            </div>
            <div
              className={
                'cell small-8 tablet-portrait-shrink text-right margin-top-small-16 margin-top-tablet-portrait-0'
              }>
              {isPaused ? (
                <Button
                  action={() => gotoNextStepOrComplete()}
                  icon={{ side: 'right', value: IconsEnum.arrowRight }}
                  type={'button'}
                  variant={'solid'}
                  size={'medium'}
                  className={classes.NextButton}
                  color={ButtonColorsEnum.white}>
                  {stepsCompleted ? 'Complete Tour' : 'Continue Tour'}
                </Button>
              ) : (
                <Button
                  action={() => gotoNextStepOrComplete()}
                  icon={{ side: 'right', value: IconsEnum.arrowRight }}
                  type={'button'}
                  variant={'solid'}
                  size={'medium'}
                  className={classes.NextButton}
                  color={ButtonColorsEnum.white}>
                  {isLastStep ? 'Finish' : stepsCompleted ? 'Complete Tour' : 'Next'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
